/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import {Listbox} from '@headlessui/react'
import {CheckIcon} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import {UiSelectOption} from '../UiSelectWithGraphics'

interface UiSelectWithGraphicsOptionProps {
    option: UiSelectOption
    wrapOptionLabels?: boolean
}

const UiSelectWithGraphicsOption: React.FC<UiSelectWithGraphicsOptionProps> = ({
    option,
    wrapOptionLabels = false,
}) => {
    const getColorByOptionCategory = (option: UiSelectOption) => {
        switch (option.category) {
            case 'A':
                return 'bg-blue-200'
            case 'B':
                return 'bg-blue-300'
            case 'C':
                return 'bg-blue-400'
            default:
                return ''
        }
    }
    return (
        <Listbox.Option
            key={option.value}
            value={option}
            className={({ active }) =>
                clsx(
					'UiSelectWithGraphicsOption',
					getColorByOptionCategory(option),
                    active ? 'text-white bg-indigo-600 dark:bg-indigo-500' : 'text-gray-900 dark:text-gray-100',
                    'cursor-default select-none relative py-2 pl-3 pr-9'
                )
            }
        >
            {({ active, selected }) => (
                <>
                    <div
                        className={clsx(
                            option.className,
                            'flex flex-col flex-1 overflow-hidden'
                        )}
                    >
                        <span
                            className={clsx(
                                selected ? 'font-semibold' : 'font-normal',
                                wrapOptionLabels
                                    ? 'whitespace-normal wrap-break-word leading-snug pr-6'
                                    : 'whitespace-nowrap truncate'
                            )}
                        >
                            {option.label}
                        </span>

                        {option.description && (
                            <span
                                className={clsx(
                                    active
                                        ? 'text-indigo-200 dark:text-indigo-100'
                                        : 'text-gray-700 dark:text-gray-300',
                                    'text-left whitespace-nowrap truncate'
                                )}
                            >
                                <em dangerouslySetInnerHTML={{__html: option.description}} />
                            </span>
                        )}
                    </div>

                    {selected && (
                        <span
                            className={clsx(
                                active ? 'text-white' : 'text-indigo-600',
                                'absolute inset-y-0 right-0 flex items-center pr-4'
                            )}
                        >
                            <CheckIcon className={clsx('h-5 w-5')} />
                        </span>
                    )}
                </>
            )}
        </Listbox.Option>
    )
}

export default UiSelectWithGraphicsOption
